import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';

// Modern (jjfv2) layout — same header/hero/footer as the About Us pages
import HeaderV2 from '../../components/headerV2/HeaderV2';
import FooterV2 from '../../components/footerV2/FooterV2';
import AboutHero from '../../components/aboutHero/AboutHero';
import useScrollReveal from '../../hooks/useScrollReveal';

import BlogCard from './BlogCard';

import { fetchBlogs } from '../../api/cmsApi';

// CSS imports
import '../AboutUsPage/aboutV2.css';
import './blogs.css';

// Blog image imports
import HumunityImage from "./../../images/blogs/humanity_blog.webp"
import DiyaImage from "./../../images/blogs/diyas_blog.webp"
import HeartOfService from "./../../images/blogs/heart_of_service_blog.webp"
import PulseOfChange from "./../../images/blogs/the_pulse_of_change.webp"
import GratitudeImage from "./../../images/blogs/when_gratitude_finds_blog.webp";
import SeedOfChangeImage from "./../../images/blogs/seed_of_change.webp"

const blogs = [
    {
        id: "bl-01",
        title: "Where Humanity Finds Its Purpose",
        desc: "Hero? No. I’m just doing what I should. Explore the heart of JJFIndia and our mission to make service a shared human responsibility.",
        img: HumunityImage,
        link: "/our-bulletin/where-humanity-finds-its-purpose",
    },
    {
        id: "bl-02",
        title: "When Diyas Meet Hearts: Celebrating Diwali with JJFIndia",
        desc: "Experience Diwali with JJFIndia at Mowli Balak Ashram. Discover how the Jainam Jivika Foundation is lighting lives through connection, care, and the gift of time.",
        img: DiyaImage,
        link: "/our-bulletin/when-diyas-meet-hearts",
    },
    {
        id: "bl-03",
        title: "100,000 Hearts of Service: JJFIndia Reaches a National Volunteer Milestone",
        desc: "Kindness is a responsibility, not just a feeling. Celebrate the 100,000+ volunteers of JJFIndia’s SevakArmy bringing change through the power of Seva.",
        img: HeartOfService,
        link: "/our-bulletin/hearts-of-service",
    },
    {
        id: "bl-04",
        title: " The Pulse of Change Beats Through Every Hand",
        desc: "From green initiatives to mental health support, JJFIndia (Jainam Jivika Foundation) works to address today’s challenges and build a better future for everyone.",
        img: PulseOfChange,
        link: "/our-bulletin/the-pulse-of-change-beats",
    },
    {
        id: "bl-05",
        title: "When Gratitude Finds Its Way Back",
        desc: "At JJFIndia, we believe service is a shared pause between lives. Explore our story of quiet connection and food distribution at Sankatmochan Temple.",
        img: GratitudeImage,
        link: "/our-bulletin/when-gratitude-finds",
    },
    {
        id: "bl-06",
        title: "Seeds of Change: How Young Minds in Varanasi Are Growing a Greener Tomorrow",
        desc: "JJFIndia partners with Shandilya Public School, Varanasi, for an environmental awareness program, distributing 200+ seeds to the next generation of planet protectors.",
        img: SeedOfChangeImage,
        link: "/our-bulletin/seeds-of-change",
    }
]

// The six blogs above are hand-built pages and stay that way. Anything newer is
// published from the CMS, so skip a CMS post that duplicates one of them.
const staticLinks = new Set(blogs.map(blog => blog.link));

export default function Blogs() {
    const rootRef = useRef(null);
    useScrollReveal(rootRef);

    const [cmsBlogs, setCmsBlogs] = useState([]);

    useEffect(() => {
        const controller = new AbortController();

        fetchBlogs({ signal: controller.signal })
            .then((posts) => {
                if (controller.signal.aborted) return;
                setCmsBlogs(posts.filter(post => !staticLinks.has(`/our-bulletin/${post.slug}`)));
            })
            .catch((error) => {
                if (controller.signal.aborted || error.name === 'AbortError') return;
                // A CMS outage should never blank out the existing blogs
                console.error('Could not load blogs from the CMS:', error);
            });

        return () => controller.abort();
    }, []);

    return (
        <>
            <Helmet>
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
                <link
                    href="https://fonts.googleapis.com/css2?family=Anton&family=Inter:wght@400;500;600;700;800&display=swap"
                    rel="stylesheet"
                />

                {/* Basic Meta Tags */}
                <title>Words born from service and empathy | JJF India Blogs </title>
                <meta name="description" content="Explore the heart of JJFIndia. From the streets of Varanasi to environmental drives, read stories of service, empathy, and our shared human responsibility." />
                <meta name="keywords" content="JJFIndia, Jainam Jivika Foundation, Jainam and Jivika, Service as responsibility, Humanity first NGO, Culture of Seva" />
                <meta name="robots" content="index, follow" />

                {/* Canonical URL */}
                <link rel="canonical" href="https://jjfindia.com/our-bulletin/blogs" />

                {/* Open Graph */}
                <meta property="og:title" content="Words born from service and empathy | JJF India Blogs" />
                <meta property="og:description" content="Explore the heart of JJFIndia. From the streets of Varanasi to environmental drives, read stories of service, empathy, and our shared human responsibility." />
                <meta property="og:url" content="https://jjfindia.com/our-bulletin/blogs" />

                {/* Twitter Cards */}
                <meta name="twitter:title" content="Words born from service and empathy | JJF India Blogs" />
                <meta name="twitter:description" content="Explore the heart of JJFIndia. From the streets of Varanasi to environmental drives, read stories of service, empathy, and our shared human responsibility." />
            </Helmet>

            <div className="jjfv2" ref={rootRef}>
                <HeaderV2 />

                <main className="page_content">
                    {/* ---------- Page hero (same as About Us pages) ---------- */}
                    <AboutHero
                        eyebrow="Our Bulletin"
                        title="Blogs"
                        crumbs={[{ label: 'Home', to: '/' }, { label: 'Blogs' }]}
                    />

                    {/* ---------- Blog listing ---------- */}
                    <section className="section">
                        <div className="wrap">
                            <div className="about-head reveal">
                                <span className="eyebrow">Words born from service and empathy</span>
                                <h2>Stories That Inspire Change</h2>
                            </div>

                            <div className="blog-container">
                                {blogs.map(blog => (
                                    <div
                                        key={blog.id}
                                        style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}
                                    >
                                        <BlogCard
                                            img={blog.img}
                                            title={blog.title}
                                            desc={blog.desc}
                                            link={blog.link}
                                        />
                                    </div>
                                ))}

                                {/* Newer blogs, published from the CMS */}
                                {cmsBlogs.map(blog => (
                                    <div
                                        key={`cms-${blog.id}`}
                                        style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}
                                    >
                                        <BlogCard
                                            img={blog.thumbnail}
                                            title={blog.title}
                                            desc={blog.excerpt}
                                            link={`/our-bulletin/${blog.slug}`}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                </main>

                <FooterV2 />
            </div>
        </>
    );
}
